import { ContextManager, QueryResponse, ScopeContext, SignalEvent, useScope } from 'dreamstate';
import { useContext, useEffect, useLayoutEffect } from 'react';

import { AnyObject, Optional } from '@vimeo/core/types/generic';
import { Viewer, ViewerContext } from '@vimeo/viewer';

// todo: Compatibility with ESignal for constants.
export const VIMEO_VIEWER_READY_CONTEXT_SIGNAL: string = '@VIMEO/VIEWER_READY';

export const VIMEO_VIEWER_CONTEXT_QUERY: string = '@VIMEO/VIEWER_CONTEXT_QUERY';

export type TOptionalViewerQueryResponse = QueryResponse<Optional<Viewer>>;

export type TViewerQueryResponse = QueryResponse<Viewer>;

export type TViewerReadySignalEvent = SignalEvent<Viewer>;

/**
 * Provide the current viewer to all requesting entities in scope.
 */
export function useScopeViewerProvision(): void {
  const scope: ScopeContext = useScope();
  const viewer: Optional<Viewer> = useContext(ViewerContext);

  /*
   * Provide viewer as response for all query request in scope.
   * Allows bundling together
   */
  useLayoutEffect(
    () => scope.registerQueryProvider(VIMEO_VIEWER_CONTEXT_QUERY, () => viewer),
    [scope, viewer],
  );
}

/**
 * Emit current viewer changes to all requesting entities in scope.
 */
export function useScopeViewerEmitting(): void {
  const { emitSignal } = useScope();
  const viewer = useContext(ViewerContext);

  useEffect(() => {
    if (viewer) {
      emitSignal({ type: VIMEO_VIEWER_READY_CONTEXT_SIGNAL, data: viewer });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitSignal, viewer?.user]);
}

/**
 * Query viewer in the current manager.
 * Shortcut to access the viewer object in a sync way.
 *
 * @param manager - target manager to query viewer object for
 * @return viewer object from current context
 */
export function queryCurrentViewerSync(manager: ContextManager<AnyObject, AnyObject>): Viewer {
  const response: TViewerQueryResponse = manager.queryDataSync({
    type: VIMEO_VIEWER_CONTEXT_QUERY,
  });

  return response?.data;
}

/**
 * Query viewer in the current manager.
 * Shortcut to access the viewer object in a sync way.
 *
 * @param manager - target manager to query viewer object for
 * @return optional viewer object from current context
 */
export function queryOptionalViewerSync(
  manager: ContextManager<AnyObject, AnyObject>,
): Optional<Viewer> {
  const response: TOptionalViewerQueryResponse = manager.queryDataSync({
    type: VIMEO_VIEWER_CONTEXT_QUERY,
  });

  return response?.data;
}
